/**
 * Motor de calendario en el cliente (§4.4). Lógica compartida.
 *
 * Todo el cálculo corre acá, en el navegador, sobre la copia local. Es lo que permite preguntar
 * "¿qué pasa si no voy el jueves?" y ver la respuesta al instante, sin conexión: la simulación
 * es sólo recalcular con un día bloqueado de más y comparar los dos resultados.
 */

import { useCallback, useMemo, useState } from 'react';
import {
  addDias,
  calcularCalendario,
  diaSemana,
  estadoPlan,
  explicarCambios,
  nombreMusculo,
  rangoFechas,
  simularBloqueo,
  type CambioCalendario,
  type EntradaMotor,
  type Fecha,
  type PlanCalendario,
} from '@mg/core';

import { bloquearDias, desbloquearDia } from './acciones.ts';
import { useDatos } from './useDatos.ts';

export interface DiaCalendario {
  fecha: Fecha;
  esHoy: boolean;
  cerrado: boolean;
  bloqueado: { id: string; motivo: string } | null;
  descanso: boolean;
  entrada: PlanCalendario['entradas'][number] | null;
  sesionReal: { id: string; estado: string } | null;
  dentroDelPlan: boolean;
}

export interface AvisoPlan {
  clave: string;
  tono: 'importante' | 'atencion' | 'info';
  texto: string;
}

/**
 * "15/08", que es como se lee una fecha en una lista de fechas.
 *
 * Es EL formato corto de la pantalla de Plan y por eso se exporta: la misma vista llegó a mostrar
 * "vence el 2026-09-12", "15/08" y "2026-08-23 · Bloqueado" a la vez, y tres formatos de fecha en
 * una pantalla se leen como tres cosas distintas. Lo corto va en dd/mm; lo que es una frase
 * ("Si faltás el…", "Último día en que se puede entrenar") va en `fechaLarga`.
 */
export const diaMes = (f: Fecha): string => `${f.slice(8)}/${f.slice(5, 7)}`;

/** "15/08 y 01/09", "15/08, 01/09 y 20/09". */
function enumerar(fechas: Fecha[]): string {
  const partes = fechas.map(diaMes);
  if (partes.length <= 1) return partes.join('');
  return `${partes.slice(0, -1).join(', ')} y ${partes[partes.length - 1]!}`;
}

/**
 * Qué falta para poder bloquear (patrón `queFalta` de useOnboarding, flujo.md §3).
 *
 * "Bloquear y recalcular" arranca deshabilitado porque las dos fechas vienen vacías, y un botón
 * gris que no dice por qué es una pared: el usuario no tiene forma de saber que le faltan las
 * fechas y no que la pantalla está rota.
 */
export function queFaltaBloqueo(desde: string, hasta: string): string | null {
  if (!desde && !hasta) return 'Elegí desde y hasta.';
  if (!desde) return 'Elegí desde qué día.';
  if (!hasta) return 'Elegí hasta qué día.';
  if (hasta < desde) return 'El "hasta" no puede ser anterior al "desde".';
  return null;
}

export function useCalendario() {
  const datos = useDatos();
  const [ultimosCambios, setUltimosCambios] = useState<CambioCalendario[]>([]);
  const [simulando, setSimulando] = useState<{ fecha: Fecha; cambios: CambioCalendario[] } | null>(null);

  const entrada = useMemo<EntradaMotor | null>(() => {
    if (!datos.planActivo || !datos.gimnasioActivo) return null;
    return {
      plan: datos.planActivo,
      gimnasio: datos.gimnasioActivo,
      diasBloqueados: datos.diasBloqueados,
      rutinaDias: datos.rutinaDias,
      ejerciciosPorDia: datos.ejerciciosPorDia,
      sesiones: datos.sesiones,
      hoy: datos.hoy,
    };
  }, [datos]);

  const calendario = useMemo(() => (entrada ? calcularCalendario(entrada) : null), [entrada]);
  const plan = useMemo(
    () => (datos.planActivo ? estadoPlan(datos.planActivo, datos.sesiones, datos.hoy) : null),
    [datos.planActivo, datos.sesiones, datos.hoy],
  );

  /** La grilla de días para pintar, desde el inicio del plan hasta el vencimiento. */
  const dias = useMemo<DiaCalendario[]>(() => {
    if (!datos.planActivo || !datos.gimnasioActivo || !calendario) return [];

    const cierres = new Set(datos.gimnasioActivo.dias_cierre_semanal);
    const porFecha = new Map(calendario.entradas.map((e) => [e.fecha, e]));
    const descansos = new Set(
      calendario.noDisponibles.filter((d) => d.motivo === 'descanso_recuperacion').map((d) => d.fecha),
    );
    const bloqueos = new Map(
      datos.diasBloqueados
        .filter((b) => b.gimnasio_id === datos.planActivo!.gimnasio_id)
        .map((b) => [b.fecha, { id: b.id, motivo: b.motivo }]),
    );
    const sesionesPorFecha = new Map(datos.sesiones.map((s) => [s.fecha, { id: s.id, estado: s.estado }]));

    return rangoFechas(datos.planActivo.fecha_inicio, datos.planActivo.fecha_vencimiento).map((fecha) => ({
      fecha,
      esHoy: fecha === datos.hoy,
      cerrado: cierres.has(diaSemana(fecha)),
      bloqueado: bloqueos.get(fecha) ?? null,
      descanso: descansos.has(fecha),
      entrada: porFecha.get(fecha) ?? null,
      sesionReal: sesionesPorFecha.get(fecha) ?? null,
      dentroDelPlan: true,
    }));
  }, [datos, calendario]);

  /**
   * Los avisos que se pintan, ya sin eco.
   *
   * El motor emite un aviso por cada día en conflicto, y la recomendación completa vuelve a
   * aparecer en la fila del día y en la celda del calendario: la misma frase llegaba a leerse tres
   * veces. Acá los conflictos se colapsan en un aviso que cuenta los días, NOMBRA EL MÚSCULO y
   * nombra las fechas; el qué hacer queda en el día, que es donde se actúa.
   *
   * El músculo sale de `conflicto.musculos`, que es lo que dice el motor: si algún día viniera sin
   * músculos, el aviso vuelve a la frase genérica en vez de quedar cortado.
   *
   * Va en ámbar y no en agua: el verde-agua de `info` se lee "todo bien", que es exactamente lo
   * contrario de lo que dice un día que encadena el mismo músculo dos veces seguidas.
   */
  const avisos = useMemo<AvisoPlan[]>(() => {
    if (!calendario) return [];

    const out: AvisoPlan[] = [];
    for (const a of calendario.avisos) {
      if (a.tipo === 'conflicto_recuperacion') continue;
      out.push({
        clave: a.tipo + a.texto,
        tono: a.tipo === 'colchon_critico' ? 'atencion' : 'importante',
        texto: a.texto,
      });
    }

    const enConflicto = calendario.entradas.filter((e) => e.conflicto !== null);
    if (enConflicto.length > 0) {
      const fechas = enConflicto.map((e) => e.fecha);
      const musculos = [...new Set(enConflicto.flatMap((e) => e.conflicto!.musculos))].map(nombreMusculo);
      const cuales = musculos.length > 0 ? musculos.join(', ') : 'el mismo músculo';
      out.push({
        clave: 'conflictos',
        tono: 'atencion',
        texto:
          enConflicto.length === 1
            ? `1 día encadena ${cuales} con el día anterior: ${enumerar(fechas)}.`
            : `${enConflicto.length} días encadenan ${cuales} con el día anterior: ${enumerar(fechas)}.`,
      });
    }

    return out;
  }, [calendario]);

  /** Simula perder un día sin tocar nada: devuelve las frases del cambio. */
  const simular = useCallback(
    (fecha: Fecha) => {
      if (!entrada) return;
      const { cambios } = simularBloqueo(entrada, fecha, 'simulación');
      setSimulando({ fecha, cambios });
    },
    [entrada],
  );

  const bloquear = useCallback(
    async (desde: Fecha, hasta: Fecha, motivo: string): Promise<void> => {
      if (!entrada || !datos.perfil) return;
      const antes = calcularCalendario(entrada);
      const fechas = rangoFechas(desde, hasta);
      await bloquearDias(datos.perfil.id, entrada.plan.gimnasio_id, fechas, motivo || 'Bloqueado');

      const despues = calcularCalendario({
        ...entrada,
        diasBloqueados: [
          ...entrada.diasBloqueados,
          ...fechas.map((fecha) => ({
            id: `b-${datos.perfil!.id}-${fecha}`,
            user_id: datos.perfil!.id,
            gimnasio_id: entrada.plan.gimnasio_id,
            fecha,
            motivo: motivo || 'Bloqueado',
          })),
        ],
      });
      setUltimosCambios(explicarCambios(antes, despues));
      setSimulando(null);
    },
    [entrada, datos.perfil],
  );

  const liberar = useCallback(
    async (id: string, fecha: Fecha): Promise<void> => {
      if (!entrada) return;
      const antes = calcularCalendario(entrada);
      await desbloquearDia(id);
      const despues = calcularCalendario({
        ...entrada,
        diasBloqueados: entrada.diasBloqueados.filter((b) => b.id !== id),
      });
      setUltimosCambios(explicarCambios(antes, despues));
      void fecha;
    },
    [entrada],
  );

  return {
    datos,
    calendario,
    plan,
    dias,
    avisos,
    ultimosCambios,
    limpiarCambios: () => setUltimosCambios([]),
    simulando,
    simular,
    limpiarSimulacion: () => setSimulando(null),
    bloquear,
    liberar,
    manana: addDias(datos.hoy, 1),
  };
}
